/**
 * Loading screen shown between intake submit and the development-plan reveal.
 *
 * The plan-generation API call usually takes 2–5 seconds. We use that time
 * to walk the user through three named "checkpoints" so the wait feels
 * meaningful rather than blank — and so the eventual reveal feels earned.
 *
 * The screen is purely visual: it advances to the final active checkpoint on
 * a fixed cadence, then waits there. The parent replaces this screen only when
 * the real API call finishes, so visual completion never races actual work.
 */
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

import AppHeaderBar from '../layout/AppHeaderBar';
import { useTranslation } from '../../hooks/useTranslation';
import { useResponsive } from '../../hooks/useResponsive';
import {
  ONBOARDING_COLORS as COLORS,
  ONBOARDING_CONTENT_MAX_WIDTH,
  ONBOARDING_CONTROL_HEIGHT,
  ONBOARDING_RADII,
  ONBOARDING_SPACING,
  ONBOARDING_TYPOGRAPHY,
} from './onboardingTheme';

const CHECKPOINT_KEYS = ['readingProfile', 'mappingFocus', 'draftingPlan'] as const;
const CHECKPOINT_INTERVAL_MS = 900;

interface Props {
  onComplete?: () => void;
}

export default function OnboardingLoadingScreen({ onComplete: _onComplete }: Props) {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { isPhone } = useResponsive();
  // Number of completed checkpoints; the indexed checkpoint remains active.
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const ids = CHECKPOINT_KEYS.slice(1).map((_, index) =>
      setTimeout(() => setProgress(index + 1), CHECKPOINT_INTERVAL_MS * (index + 1)),
    );
    return () => ids.forEach(clearTimeout);
  }, []);

  const activeCheckpointLabel = t(`intake.loading.checkpoints.${CHECKPOINT_KEYS[progress]}`);

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: COLORS.surface }}>
      <AppHeaderBar />
      <ScrollView
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          paddingHorizontal: ONBOARDING_SPACING.xl,
          paddingTop: ONBOARDING_SPACING.xxl,
          paddingBottom: Math.max(
            insets.bottom + ONBOARDING_SPACING.xxl,
            ONBOARDING_SPACING.xl * 2,
          ),
        }}
        showsVerticalScrollIndicator={false}
      >
        <View style={{ width: '100%', maxWidth: ONBOARDING_CONTENT_MAX_WIDTH, alignSelf: 'center' }}>
          <Card style={{ borderRadius: ONBOARDING_RADII.lg }}>
            <CardContent
              style={{
                gap: ONBOARDING_SPACING.xl,
                padding: isPhone ? ONBOARDING_SPACING.xl : ONBOARDING_SPACING.xxl,
              }}
            >
              <View
                style={{
                  flexDirection: isPhone ? 'column' : 'row',
                  alignItems: isPhone ? 'flex-start' : 'center',
                  gap: ONBOARDING_SPACING.sm,
                }}
              >
                <View
                  className="items-center justify-center"
                  style={{
                    width: ONBOARDING_CONTROL_HEIGHT,
                    height: ONBOARDING_CONTROL_HEIGHT,
                    borderRadius: ONBOARDING_RADII.lg,
                    backgroundColor: COLORS.primary,
                  }}
                >
                  <MaterialIcons
                    name="auto-awesome"
                    size={ONBOARDING_SPACING.xl}
                    color={COLORS.onPrimary}
                  />
                </View>
                <View style={{ flex: isPhone ? undefined : 1, minWidth: 0 }}>
                  <Text style={{ ...ONBOARDING_TYPOGRAPHY.title, color: COLORS.text.primary }}>
                    {t('intake.loading.title')}
                  </Text>
                  <Text
                    style={{
                      ...ONBOARDING_TYPOGRAPHY.bodySmall,
                      color: COLORS.text.secondary,
                      marginTop: ONBOARDING_SPACING.xs,
                    }}
                  >
                    {t('intake.loading.subtitle')}
                  </Text>
                </View>
              </View>

              <View
                style={{ gap: ONBOARDING_SPACING.lg }}
                accessible
                accessibilityRole="progressbar"
                accessibilityLabel={t('intake.loading.title')}
                accessibilityValue={{
                  min: 0,
                  max: CHECKPOINT_KEYS.length,
                  now: progress,
                  text: activeCheckpointLabel,
                }}
              >
                {CHECKPOINT_KEYS.map((key, idx) => (
                  <Checkpoint
                    key={key}
                    label={t(`intake.loading.checkpoints.${key}`)}
                    state={progress > idx ? 'done' : progress === idx ? 'active' : 'pending'}
                  />
                ))}
              </View>

              <View
                className="flex-row items-start"
                style={{
                  borderRadius: ONBOARDING_RADII.sm,
                  padding: ONBOARDING_SPACING.md,
                  backgroundColor: COLORS.primarySubtle,
                }}
              >
                <MaterialIcons
                  name="info-outline"
                  size={ONBOARDING_SPACING.lg}
                  color={COLORS.primary}
                  style={{
                    marginTop: ONBOARDING_SPACING.xs / 2,
                    marginRight: ONBOARDING_SPACING.sm,
                  }}
                />
                <Text
                  style={{
                    ...ONBOARDING_TYPOGRAPHY.caption,
                    color: COLORS.text.secondary,
                    flex: 1,
                  }}
                >
                  {t('intake.loading.tip')}
                </Text>
              </View>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

interface CheckpointProps {
  label: string;
  state: 'pending' | 'active' | 'done';
}

function Checkpoint({ label, state }: CheckpointProps) {
  const dotBg =
    state === 'done' ? COLORS.success : state === 'active' ? COLORS.primary : COLORS.border;
  return (
    <View
      className="flex-row items-center"
      accessible
      accessibilityLabel={label}
      accessibilityState={{ busy: state === 'active' }}
      accessibilityLiveRegion={state === 'active' ? 'polite' : 'none'}
    >
      <View
        className="items-center justify-center"
        accessible={false}
        style={{
          width: ONBOARDING_SPACING.xxl,
          height: ONBOARDING_SPACING.xxl,
          borderRadius: ONBOARDING_RADII.pill,
          marginRight: ONBOARDING_SPACING.md,
          backgroundColor: dotBg,
        }}
      >
        {state === 'done' ? (
          <MaterialIcons name="check" size={ONBOARDING_SPACING.lg} color={COLORS.onPrimary} />
        ) : state === 'active' ? (
          <ActivityIndicator size="small" color={COLORS.onPrimary} />
        ) : null}
      </View>
      <Text
        accessible={false}
        style={{
          ...ONBOARDING_TYPOGRAPHY.bodySmall,
          color: state === 'pending' ? COLORS.text.muted : COLORS.text.primary,
          fontWeight: state === 'pending' ? '400' : '500',
          flex: 1,
          minWidth: 0,
        }}
      >
        {label}
      </Text>
    </View>
  );
}
